import { useDefaultComputerHomepage } from '#pplx/frontend/apps/ask/hooks/homepage-hooks/useDefaultComputerHomepage.js';
import { useAsiAllowedByOrganization } from '#pplx/frontend/apps/ask/organization/hooks/useAsiAllowedByOrganization.js';
import { useDefaultSearchModel } from '#pplx/frontend/apps/ask/search/hooks/useDefaultSearchModel.js';
import { usePreferredSearchModels } from '#pplx/frontend/apps/ask/settings/hooks/usePreferredSearchModels.js';
import { useSearchModels } from '#pplx/frontend/ask-models/hooks/useSearchModels.js';
import { SearchMode, SearchModel } from '#pplx/frontend/ask-models/types/search-modes.js';
import { useIsUserLoggedIn, useIsUserLoggedInLoading } from '#pplx/frontend/auth/userHooks.js';
import { useMemo } from 'react';
const toConfiguredModel = (model) => model;
/**
 * Determines the initial thread configuration for Copilot/Computer and
 * reasoning model selection.
 *
 * Behavior:
 *
 * | Scenario                                      | Model                         | isLoading |
 * | --------------------------------------------- | ----------------------------- | --------- |
 * | Auth/default-mode inputs are still loading    | Search placeholder            | true      |
 * | Logged-out user                               | Search                        | false     |
 * | Sidecar or Windows build                      | Search                        | false     |
 * | ASI is not allowed                            | Search                        | false     |
 * | Computer default with ASI access               | Preferred/default ASI model   | false     |
 */
export function useInitialConfiguredModel() {
    const isLoggedIn = useIsUserLoggedIn();
    const isLoggedInLoading = useIsUserLoggedInLoading();
    const { getPreferredSearchModel } = usePreferredSearchModels();
    const defaultSearchModel = useDefaultSearchModel();
    const { defaultAskInputModeIsComputer, defaultAskInputModeIsLoading } = useDefaultComputerHomepage();
    const { getDefaultModelForMode } = useSearchModels({
        reason: 'initial-configured-model',
    });
    const asiAllowed = useAsiAllowedByOrganization();
    return useMemo(() => {
        let configuredModel = SearchModel.DEFAULT;
        if (isLoggedInLoading) {
            return {
                model: configuredModel,
                isLoading: true,
            };
        }
        if (defaultAskInputModeIsLoading) {
            return {
                model: configuredModel,
                isLoading: true,
            };
        }
        // Logged out users can't change the search mode
        if (!isLoggedIn) {
            return {
                model: configuredModel,
                isLoading: false,
            };
        }
        // Default to Computer when the effective user/org setting asks for it.
        // Skip in sidecar and Windows builds — both only support search/browser_agent modes.
        if (!import.meta.env.VITE_IS_SIDECAR_BUILD &&
            !import.meta.env.VITE_IS_PERPLEXITY_DESKTOP_BUILD &&
            defaultAskInputModeIsComputer &&
            asiAllowed) {
            const preferredOrDefaultAsiModel = getPreferredSearchModel(SearchMode.ASI) ?? getDefaultModelForMode(SearchMode.ASI);
            configuredModel = toConfiguredModel(preferredOrDefaultAsiModel);
            return {
                model: configuredModel,
                isLoading: false,
            };
        }
        const preferredModel = getPreferredSearchModel(SearchMode.SEARCH);
        const preferredOrDefaultSearchModel = preferredModel ?? defaultSearchModel;
        configuredModel = toConfiguredModel(preferredOrDefaultSearchModel);
        return {
            model: configuredModel,
            isLoading: false,
        };
    }, [
        isLoggedIn,
        isLoggedInLoading,
        asiAllowed,
        getPreferredSearchModel,
        defaultSearchModel,
        defaultAskInputModeIsComputer,
        defaultAskInputModeIsLoading,
        getDefaultModelForMode,
    ]);
}
//# sourceMappingURL=useInitialConfiguredModel.js.map